import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BRAND } from '../config/brand';

const SEO = ({
    title,
    description,
    keywords = [],
    image,
    imageAlt = '',
    preloads = [],
    url,
    categoryPath,
    type = 'website',
    author,
    publishedTime,
    modifiedTime,
    section,
    tags = [],
    locale = 'ru_RU',
    alternateLocales = [],
    noindex = false,
    nofollow = false,
    canonical,
    structuredData
}) => {
    // Standardwerte für russische SEO
    const defaultTitle = `${BRAND.name} - ${BRAND.claimRu}`;
    const defaultDescription = BRAND.claimRu;
    const defaultKeywords = [
        // artistix.ru domain: каталог творческих людей/коллективов
        'artistix', 'артистикс', 'artistix.ru',
        'каталог', 'каталог специалистов',
        'творческие специалисты', 'творческие коллективы',
        'художники', 'фотографы', 'дизайнеры', 'архитекторы',
        'портфолио', 'контакты',
        'поиск по категориям', 'категории', 'профили'
    ];

    const buildCategoryLine = (path) => {
        if (!path || typeof path !== 'object') return '';
        const names = [path?.main?.name, path?.sub?.name, path?.subSub?.name]
            .map((v) => (v == null ? '' : String(v).trim()))
            // normalize common separators to spaces (user wants no "/" in title)
            .map((v) => v.replace(/\s*\/\s*/g, ' '))
            .filter(Boolean);
        return names.join(' ');
    };

    const stripProfileSuffix = (val) => {
        if (!val) return '';
        const s = String(val);
        return s.replace(/\s*—\s*Профиль\s*$/u, '').trim();
    };

    const categoryLine = buildCategoryLine(categoryPath);

    const finalTitle = title
        ? (categoryLine
            ? `${stripProfileSuffix(title)} Art-каталог ${categoryLine} | ${BRAND.name}`
            : `${title} | ${BRAND.name}`)
        : defaultTitle;
    const profileTitle = title ? stripProfileSuffix(title) : finalTitle;
    const profileSlogan = description || defaultDescription;
    const finalDescription = type === 'profile'
        ? `artistiX представляет: ${profileTitle}${profileSlogan ? `. ${profileSlogan}` : ''}`
        : (description || defaultDescription);
    const finalKeywords = [...new Set([...defaultKeywords, ...keywords])].join(', ');
    const finalAuthor = author || BRAND.name;

    const toAbsolute = (val, fallback) => {
        if (!val) return fallback;
        if (/^https?:\/\//i.test(String(val))) return val;
        const base = BRAND.baseUrl;
        const path = String(val);
        return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
    };

    const finalUrl = toAbsolute(url, BRAND.baseUrl);
    const finalImage = toAbsolute(image, `${BRAND.baseUrl}/og-image.jpg`);
    const finalImageAlt = imageAlt || finalTitle;
    const finalCanonical = canonical || finalUrl;

    // Robots Meta
    const robots = [];
    if (noindex) robots.push('noindex');
    if (nofollow) robots.push('nofollow');
    if (robots.length === 0) robots.push('index', 'follow');

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{finalTitle}</title>
            <meta name="title" content={finalTitle} />
            <meta name="description" content={finalDescription} />
            <meta name="keywords" content={finalKeywords} />
            <meta name="author" content={finalAuthor} />
            <meta name="robots" content={robots.join(', ')} />


            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={finalUrl} />
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={finalDescription} />
            <meta property="og:image" content={finalImage} />
            {finalImageAlt && <meta property="og:image:alt" content={finalImageAlt} />}
            <meta property="og:site_name" content={BRAND.name} />
            <meta property="og:locale" content={locale} />
            {publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
            {section && <meta property="article:section" content={section} />}
            {tags.map(tag => (
                <meta key={tag} property="article:tag" content={tag} />
            ))}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={finalUrl} />
            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={finalDescription} />
            <meta name="twitter:image" content={finalImage} />
            {finalImageAlt && <meta name="twitter:image:alt" content={finalImageAlt} />}
            {/* twitter handle unknown for artistix: keep meta absent rather than wrong */}

            {/* Additional SEO Meta Tags (kept minimal in SEO.js; PWA meta in public/index.html) */}

            {/* Performance & Security (managed via HTTP headers/Helmet) */}

            {/* Canonical URL */}
            <link rel="canonical" href={finalCanonical} />

            {/* Preload main image(s) if provided */}
            {preloads && preloads.map((href, i) => (
                <link key={`preload-${i}`} rel="preload" as="image" href={href} />
            ))}

            {/* Hreflang */}
            <link rel="alternate" hrefLang="ru-RU" href={finalCanonical} />
            <link rel="alternate" hrefLang="x-default" href={finalCanonical} />
            {/* Alternate Languages */}
            {Array.isArray(alternateLocales) && alternateLocales.map((alt, i) => {
                if (!alt) return null;
                if (typeof alt === 'string') {
                    return <link key={`alt-${alt}-${i}`} rel="alternate" hrefLang={alt} href={finalCanonical} />;
                }
                const lang = alt.lang || alt.locale || alt.hreflang || alt.hrefLang || alt.code;
                const href = alt.href || alt.url || finalCanonical;
                if (!lang) return null;
                return <link key={`alt-${lang}-${i}`} rel="alternate" hrefLang={lang} href={href} />;
            })}

            {/* Structured Data (Schema.org) */}
            {structuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            )}

            {/* Default Structured Data if none provided */}
            {!structuredData && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        // Minimal default JSON-LD without fake ratings.
                        // Pages that need richer schema should pass `structuredData` explicitly.
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "WebSite",
                                "name": BRAND.name,
                                "url": BRAND.baseUrl,
                                "inLanguage": "ru-RU",
                                "potentialAction": {
                                    "@type": "SearchAction",
                                    "target": `${BRAND.baseUrl}/search?q={search_term_string}`,
                                    "query-input": "required name=search_term_string"
                                }
                            },
                            {
                                "@type": "WebPage",
                                "name": finalTitle,
                                "description": finalDescription,
                                "url": finalCanonical,
                                "inLanguage": "ru-RU",
                                "isPartOf": {
                                    "@type": "WebSite",
                                    "url": BRAND.baseUrl
                                }
                            }
                        ]
                    })}
                </script>
            )}





        </Helmet>
    );
};

export default SEO;
